import React, { useState } from "react";

const LoanCalculator: React.FC = () => {
  const [borrowAmount, setBorrowAmount] = useState(20000);
  const [selectedPeriod, setSelectedPeriod] = useState("3 years");
  const [varicashRate, setVaricashRate] = useState(10.5);

  const periods = [
    "5 years", "3 years", "2 years", "1 year",
    "9 months", "6 months", "3 months", "2 months",
    "30 days", "15 days", "1 day",
  ];

  const amountSteps = [
    10000, 20000, 50000, 100000, 200000, 500000, 1000000,
    2000000, 5000000, 10000000, 20000000, 50000000,
  ];

  const getTimeInYears = (period: string): number => {
    switch (period) {
      case "1 day": return 1 / 365;
      case "15 days": return 15 / 365;
      case "30 days": return 30 / 365;
      case "2 months": return 2 / 12;
      case "3 months": return 3 / 12;
      case "6 months": return 6 / 12;
      case "9 months": return 9 / 12;
      case "1 year": return 1;
      case "2 years": return 2;
      case "3 years": return 3;
      case "5 years": return 5;
      default: return 1;
    }
  };

  const timeInYears = getTimeInYears(selectedPeriod);
  const total_investment = borrowAmount * Math.pow(1 + 0.13, timeInYears);
  const total_paid_varicash = borrowAmount * (1 + (varicashRate / 100) * timeInYears);
  const total_savings = total_investment - total_paid_varicash;

  const loanAgainstTotal = Math.round(total_paid_varicash);
  const personalLoanTotal = Math.round(borrowAmount * (1 + 0.18 * timeInYears));
  const creditCardTotal = Math.round(borrowAmount * (1 + 0.45 * timeInYears));
  const personalLoanExtra = Math.max(personalLoanTotal - loanAgainstTotal, 0);
  const creditCardExtra = Math.max(creditCardTotal - loanAgainstTotal, 0);

  const totalAmount = Math.round(borrowAmount + (borrowAmount * varicashRate * timeInYears) / 100);

  const sliderPct = (amountSteps.indexOf(borrowAmount) / (amountSteps.length - 1)) * 100;
  const ratePct = ((varicashRate - 8) / 4) * 100;

  const f = (n: number) => Math.round(n).toLocaleString("en-IN");

  return (
    <div style={{ backgroundColor: "#fff", fontFamily: "DM Sans, sans-serif", paddingLeft: "16px", paddingRight: "16px" }}>

      {/* ── Chart Section ── */}
      <div style={{ marginBottom: "24px" }}>
        <div style={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          padding: "20px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
          borderTop: "0.5px solid #E0E0E0",
        }}>
          {/* Chart header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <span style={{ fontSize: "16px", fontWeight: "600", color: "#067A69" }}>Total Amount</span>
            <span style={{
              backgroundColor: "rgba(0,0,0,0.74)",
              color: "#fff",
              fontSize: "12px",
              fontWeight: "500",
              padding: "6px 12px",
              borderRadius: "4px",
            }}>
              Tenure: {selectedPeriod}
            </span>
          </div>

          {/* Bars */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", height: "140px", paddingLeft: "5px", paddingRight: "5px", marginTop: "25px" }}>

            {/* Bar 1 — Loan against MF */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", marginLeft: "2px", marginRight: "2px", maxWidth: "32%" }}>
              <span style={{ fontSize: "11px", fontWeight: "700", color: "#034236", marginBottom: "6px", textAlign: "center" }}>
                ₹{f(loanAgainstTotal)}
              </span>
              <div style={{ width: "100%", height: "60px", backgroundColor: "#15AB95", borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "10px" }}>
                <span style={{ color: "#fff", fontSize: "10px", fontWeight: "600" }}>₹{f(loanAgainstTotal)}</span>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ backgroundColor: "#07786B", color: "#fff", fontSize: "9px", fontWeight: "600", padding: "2px 4px", borderRadius: "2px", marginBottom: "4px" }}>
                  Interest Rate {varicashRate.toFixed(1)}%
                </div>
                <span style={{ fontSize: "9px", color: "#666", lineHeight: "11px", display: "block" }}>Loan against{"\n"}mutual fund</span>
              </div>
            </div>

            {/* Bar 2 — Personal Loan */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", marginLeft: "2px", marginRight: "2px", maxWidth: "32%" }}>
              <span style={{ fontSize: "11px", fontWeight: "700", color: "#034236", marginBottom: "6px", textAlign: "center" }}>
                ₹{f(personalLoanTotal)}
              </span>
              <div style={{ width: "100%" }}>
                <div style={{ width: "100%", height: "20px", backgroundColor: "#D9D9D9", borderRadius: "4px 4px 0 0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ color: "#000", fontSize: "8px", fontWeight: "600", textAlign: "center" }}>+ Extra ₹{f(personalLoanExtra)}</span>
                </div>
                <div style={{ width: "100%", height: "60px", backgroundColor: "#15AB95", borderRadius: "0 0 4px 4px", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "10px" }}>
                  <span style={{ color: "#fff", fontSize: "10px", fontWeight: "600" }}>₹{f(loanAgainstTotal)}</span>
                </div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ backgroundColor: "#07786B", color: "#fff", fontSize: "9px", fontWeight: "600", padding: "2px 4px", borderRadius: "2px", marginBottom: "4px" }}>
                  Interest Rate 18%
                </div>
                <span style={{ fontSize: "9px", color: "#666", lineHeight: "11px", display: "block" }}>Personal{"\n"}Loan</span>
              </div>
            </div>

            {/* Bar 3 — Credit Card */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", marginLeft: "2px", marginRight: "2px", maxWidth: "32%" }}>
              <span style={{ fontSize: "11px", fontWeight: "700", color: "#034236", marginBottom: "6px", textAlign: "center" }}>
                ₹{f(creditCardTotal)}
              </span>
              <div style={{ width: "100%" }}>
                <div style={{ width: "100%", height: "40px", backgroundColor: "#D9D9D9", borderRadius: "4px 4px 0 0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ color: "#000", fontSize: "8px", fontWeight: "600", textAlign: "center" }}>+ Extra ₹{f(creditCardExtra)}</span>
                </div>
                <div style={{ width: "100%", height: "60px", backgroundColor: "#15AB95", borderRadius: "0 0 4px 4px", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "10px" }}>
                  <span style={{ color: "#fff", fontSize: "10px", fontWeight: "600" }}>₹{f(loanAgainstTotal)}</span>
                </div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ backgroundColor: "#07786B", color: "#fff", fontSize: "9px", fontWeight: "600", padding: "2px 4px", borderRadius: "2px", marginBottom: "4px" }}>
                  Interest Rate 45%*
                </div>
                <span style={{ fontSize: "9px", color: "#666", lineHeight: "11px", display: "block" }}>Credit{"\n"}Card</span>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── Loan Calculator Section ── */}
      <div style={{ backgroundColor: "#fff", borderRadius: "16px", padding: "20px 10px", marginBottom: "24px" }}>
        <p style={{ fontSize: "20px", fontWeight: "700", color: "#034236", marginBottom: "5px" }}>Loan Calculator</p>

        {/* Borrow Amount */}
        <div style={{ marginBottom: "24px" }}>
          <p style={{ fontSize: "24px", fontWeight: "700", color: "#034236", marginBottom: "0" }}>
            ₹ {f(borrowAmount)}{" "}
            <span style={{ fontSize: "16px", fontWeight: "400" }}>Borrow Amount</span>
          </p>
          <input
            type="range" min="0" max={amountSteps.length - 1} step="1"
            value={amountSteps.indexOf(borrowAmount)}
            onChange={(e) => setBorrowAmount(amountSteps[parseInt(e.target.value)])}
            style={{
              width: "100%", height: "4px", marginTop: "10px", marginBottom: "10px", cursor: "pointer",
              background: `linear-gradient(to right, #54D4A0 0%, #54D4A0 ${sliderPct}%, #F0F0F0 ${sliderPct}%, #F0F0F0 100%)`,
              appearance: "none", borderRadius: "4px",
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: "12px", color: "#666" }}>₹10K</span>
            <span style={{ fontSize: "12px", color: "#666" }}>₹5Cr</span>
          </div>
        </div>

        {/* Interest Rate */}
        <div style={{ marginBottom: "24px" }}>
          <p style={{ fontSize: "24px", fontWeight: "700", color: "#034236", marginBottom: "0" }}>
            {varicashRate.toFixed(1)}%{" "}
            <span style={{ fontSize: "16px", fontWeight: "400" }}>Interest Rate</span>
          </p>
          <input
            type="range" min="8" max="12" step="0.1"
            value={varicashRate}
            onChange={(e) => setVaricashRate(parseFloat(e.target.value))}
            style={{
              width: "100%", height: "4px", marginTop: "10px", marginBottom: "10px", cursor: "pointer",
              background: `linear-gradient(to right, #54D4A0 0%, #54D4A0 ${ratePct}%, #F0F0F0 ${ratePct}%, #F0F0F0 100%)`,
              appearance: "none", borderRadius: "4px",
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: "12px", color: "#666", fontWeight: "500" }}>8%</span>
            <span style={{ fontSize: "12px", color: "#666", fontWeight: "500" }}>12%</span>
          </div>
        </div>

        {/* Repayment Schedule */}
        <div style={{ marginBottom: "2px" }}>
          <p style={{ fontSize: "14px", color: "#666", marginBottom: "12px" }}>Repayment Schedule</p>
          <div style={{ display: "flex", overflowX: "auto", gap: "12px", paddingBottom: "8px" }}>
            {periods.map((period) => (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                style={{
                  padding: "8px 16px",
                  borderRadius: "20px",
                  border: "none",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  backgroundColor: selectedPeriod === period ? "#15AB95" : "#f0f0f0",
                  color: selectedPeriod === period ? "#fff" : "#666",
                  fontWeight: selectedPeriod === period ? "600" : "400",
                  fontSize: "14px",
                  fontFamily: "DM Sans, sans-serif",
                  flexShrink: 0,
                }}
              >
                {period}
              </button>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: "1px", backgroundColor: "#E0E0E0", margin: "20px -10px" }} />

        {/* Results */}
        <div style={{ marginTop: "16px" }}>
          {/* Dark summary card */}
          <div style={{
            backgroundColor: "#034236",
            borderRadius: "20px",
            padding: "20px",
            marginBottom: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
          }}>
            <p style={{ fontSize: "13px", color: "rgba(255,255,255,0.7)", fontWeight: "500", margin: "0 0 4px 0" }}>You Borrow</p>
            <p style={{ fontSize: "32px", color: "#fff", fontWeight: "700", margin: "0 0 12px 0" }}>₹{f(borrowAmount)}</p>
            <div style={{ height: "1px", backgroundColor: "rgba(255,255,255,0.15)", marginBottom: "12px" }} />
            {[
              { label: "You Pay Back", value: `₹${f(totalAmount)}` },
              { label: "Interest Rate", value: `${varicashRate.toFixed(1)}% p.a.` },
              { label: "Investment Growth (13%*)", value: `₹${f(total_investment)}` },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "8px", paddingBottom: "8px" }}>
                <span style={{ fontSize: "14px", color: "rgba(255,255,255,0.8)" }}>{label}</span>
                <span style={{ fontSize: "16px", color: "#fff", fontWeight: "600" }}>{value}</span>
              </div>
            ))}
          </div>

          {/* Savings card */}
          <div style={{
            backgroundColor: "#E8F5E9",
            borderRadius: "16px",
            padding: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
          }}>
            <div>
              <p style={{ fontSize: "12px", color: "#067A69", fontWeight: "500", margin: "0 0 4px 0" }}>YOUR TOTAL SAVINGS</p>
              <p style={{ fontSize: "28px", color: "#00875A", fontWeight: "700", margin: 0 }}>₹{f(total_savings)}</p>
            </div>
            <div style={{ backgroundColor: "#00875A", padding: "6px 12px", borderRadius: "20px" }}>
              <span style={{ fontSize: "12px", color: "#fff", fontWeight: "600" }}>with Varicash</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Repayment Options ── */}
      <p style={{ fontSize: "20px", fontWeight: "700", color: "#034236", marginBottom: "5px" }}>Your total repayment amount</p>
      <p style={{ fontSize: "14px", color: "#666", marginBottom: "20px" }}>
        You're in control – repay early or later. Here's how it works
      </p>
      <div style={{
        backgroundColor: "#fff",
        borderRadius: "16px",
        padding: "20px",
        marginBottom: "24px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
        display: "flex",
        gap: "8px",
      }}>
        {/* Personal Loan */}
        <div style={{ flex: 1, padding: "12px", backgroundColor: "#fff5f5", borderRadius: "12px", border: "1px solid #fca5a5" }}>
          <p style={{ fontSize: "12px", color: "#2c2c2c", textAlign: "center", marginBottom: "12px" }}>
            Total amount you have to pay with personal loan
          </p>
          <div style={{ backgroundColor: "#F16B58", padding: "4px 8px", borderRadius: "12px", marginBottom: "12px" }}>
            <span style={{ color: "#fff", fontSize: "10px", fontWeight: "600" }}>
              Pay ₹{f(personalLoanExtra)} extra in interest
            </span>
          </div>
          <p style={{ fontSize: "16px", fontWeight: "700", color: "#2c2c2c", margin: 0 }}>₹{f(personalLoanTotal)}</p>
        </div>

        {/* Varicash */}
        <div style={{ flex: 1, padding: "12px", backgroundColor: "#f0fff4", borderRadius: "12px", border: "1px solid #86efac" }}>
          <p style={{ fontSize: "12px", color: "#2c2c2c", textAlign: "center", marginBottom: "12px" }}>
            Total amount you have to pay with{" "}
            <span style={{ color: "#07786B", fontWeight: "700" }}>Varicash</span>
          </p>
          <div style={{ backgroundColor: "#07786B", padding: "4px 8px", borderRadius: "12px", marginBottom: "12px" }}>
            <span style={{ color: "#fff", fontSize: "10px", fontWeight: "600" }}>
              Gain ₹{f(total_savings)} on your investments
            </span>
          </div>
          <p style={{ fontSize: "16px", fontWeight: "700", color: "#2c2c2c", margin: 0 }}>₹{f(loanAgainstTotal)}</p>
        </div>
      </div>

    </div>
  );
};

export default LoanCalculator;
